import React, { useEffect, useRef, useState } from 'react'
import { LogoutIcon, SettingsIcon } from '../ui/Icons'

interface UserProfileProps {
  user: {
    name?: string
    email?: string
    avatar?: string
  } | null
  isExpanded: boolean
  onLogout: () => void
}

export const UserProfile: React.FC<UserProfileProps> = ({ user, isExpanded, onLogout }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fallback values for missing user data
  const displayName = user?.name || user?.email?.split('@')[0] || 'Admin User'
  const displayEmail = user?.email || 'admin@munchies.com'
  const avatarInitial = displayName.charAt(0).toUpperCase()

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleLogout = () => {
    setIsDropdownOpen(false)
    onLogout()
  }

  return (
    <div className={`relative mt-auto pb-4 ${isExpanded ? 'px-4' : 'px-2'}`} ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className={`
          w-full flex items-center rounded-xl
          transition-colors duration-150
          hover:bg-gray-100
          focus:outline-none focus:ring-2 focus:ring-admin-orange-500 focus:ring-offset-2 focus:ring-offset-white
          ${isExpanded ? 'p-3 gap-3' : 'p-2 justify-center'}
        `}
        aria-label={isExpanded ? 'Open user menu' : `User menu for ${displayName}`}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        {/* Avatar */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center font-semibold shadow-md">
          {user?.avatar ? (
            <img src={user.avatar} alt={displayName} className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-sm">{avatarInitial}</span>
          )}
        </div>

        {/* User info - only shown when expanded (mobile) */}
        {isExpanded && (
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
            <div className="text-xs text-gray-500 truncate">{displayEmail}</div>
          </div>
        )}
      </button>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div
          className={`
            absolute bottom-full mb-2 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200/50
            py-2 z-50
            ${isExpanded ? 'left-0 right-0' : 'left-0 w-48'}
          `}
          role="menu"
          aria-orientation="vertical"
        >
          {/* User info in dropdown (always shown) */}
          <div className="px-4 py-2 border-b border-gray-200/50">
            <div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
            <div className="text-xs text-gray-500 truncate">{displayEmail}</div>
          </div>

          {/* Menu items */}
          <button
            onClick={() => setIsDropdownOpen(false)}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150 rounded-lg mx-1"
            role="menuitem"
          >
            <SettingsIcon size="sm" />
            <span>Profile Settings</span>
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-150 rounded-lg mx-1"
            role="menuitem"
          >
            <LogoutIcon size="sm" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  )
}
